import { useEffect, useRef, useState } from 'react';
import { useParams } from 'next/navigation';
import { SDKResponse } from '@commercetools/frontend-sdk';
import { clsx } from 'clsx';
import { useTheme } from 'next-themes';
import { ProductPaginatedResult } from 'shared/types/result';
import ProductBannerWrapper from 'components/HOC/product-banner-wrapper';
import ProductGridWrapper from 'components/HOC/product-banner-wrapper/components/banner-products-grid';
import { trackViewItemList, trackSelectItem, mapProductToGA4Item } from 'helpers/analytics/ga4Events';
import { fontStyleMap, positionMap } from 'helpers/constants';
import { DARK } from 'helpers/constants/events';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { resolveTextConfiguration } from 'helpers/hooks/useResolvedLocalizedObject';
import { desktop } from 'helpers/utils/screensizes';
import { useFeatures } from 'providers/feature-flag';
import { Product } from 'types/entity/product';
import { useProduct } from 'frontastic';
import { RelatedProductsProps } from './types';
import PlpProductTileWrapper from '../product-list/components/plp-product-tile-wrapper';
import { useCookies } from 'next-client-cookies';
import { LOCALE_COOKIE_NAME } from 'helpers/cookies/locale';

const RelatedProductsBanner = ({
  bgColorDark,
  bgColorLight,
  primaryHeading,
  secondaryHeading,
  headingPostion,
  data,
  text,
  categories,
}: RelatedProductsProps) => {
  const texts = text?.dataSource?.config;
  const { dataSource: queryToCall } = data;
  const { query } = useProduct();
  const [products, setProducts] = useState<Product[]>([]);
  const { resolvedTheme = 'light' } = useTheme();
  const [isDesktopSize] = useMediaQuery(desktop);
  const isDarkMode = resolvedTheme === DARK;
  const cookies = useCookies();
  const { locale: paramLocale } = useParams();
  const locale = (cookies.get(LOCALE_COOKIE_NAME) as string | undefined) ?? paramLocale;
  const resolved = resolveTextConfiguration(texts, locale);
  const {
    primaryHeadingLabel = '',
    primaryHeadingTextStyle = 'regular',
    fontSizeDesktop: titleSizeDesktop = 40,
    fontSizeMobile: titleSizeMobile = 20,
    textColor: titleColor = '#1E83BE',
    textColorDark: titleColorDark = '#E8F3F8',
  } = primaryHeading?.[0] || {};

  const {
    secondaryHeadingLabel = '',
    secondaryHeadingTextStyle = 'regular',
    fontSizeDesktop: subTitleSizeDesktop = 28,
    fontSizeMobile: subTitleSizeMobile = 18,
    textColor: subTitleColor = '#5C5C5C',
    textColorDark: subTitleColorDark = '#F5F5F5',
  } = secondaryHeading?.[0] || {};

  const headlineTextClassName = clsx([fontStyleMap[primaryHeadingTextStyle], '', 'mb-24 md:mb-36']);

  const subTextClassName = clsx([fontStyleMap[secondaryHeadingTextStyle], '']);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res: SDKResponse<ProductPaginatedResult> = await query({
          ...queryToCall,
          limit: 1,
        });
        if (
          res.isError ||
          !res.data.items[0]?.attributes?.related_products ||
          res.data.items[0]?.attributes?.related_products?.length === 0
        ) {
          setProducts([]);
        } else {
          const res2: SDKResponse<ProductPaginatedResult> = await query({
            productKeys: res.data.items[0]?.attributes?.related_products,
            skus: res.data.items[0]?.attributes?.related_products,
          });
          if (res2.isError) {
            setProducts([]);
          } else {
            setProducts((res2?.data?.items as Product[]) || []);
          }
        }
      } catch (e) {
        setProducts([]);
        console.error(e);
      }
    };
    if (queryToCall) {
      fetchProducts();
    } else {
      setProducts([]);
    }
  }, [queryToCall]);

  // GA4: view_item_list
  const viewItemListFired = useRef(false);
  const { enableGA4 } = useFeatures();
  useEffect(() => {
    if (products.length > 0 && !viewItemListFired.current) {
      const listName = 'Related Products';
      const items = products.map((product, index) =>
        mapProductToGA4Item(enableGA4(), product, { index, itemListName: listName }),
      );
      trackViewItemList(listName, items);
      viewItemListFired.current = true;
    }
  }, [products]);

  return products.length ? (
    <div className="mx-12 sm:mx-24 md:mx-75 lg:mx-76 xl:mx-0">
      <ProductBannerWrapper
        bgColor={isDarkMode ? bgColorDark : bgColorLight}
        className="container pb-44 pt-20 lg:pb-108"
      >
        <div
          className={clsx(
            'flex w-full flex-col gap-8 whitespace-normal text-wrap break-words lg:gap-4 lg:pb-16',
            positionMap[headingPostion ?? 'center'],
          )}
          data-testid="related-products-text-section"
        >
          {primaryHeadingLabel && (
            <h2
              data-testid="headline-text"
              style={{
                color: isDarkMode && titleColorDark ? titleColorDark : titleColor,
                fontSize: isDesktopSize ? titleSizeDesktop : titleSizeMobile,
              }}
              className={headlineTextClassName}
              data-ffid="lbl-related-product-banner-title"
            >
              {primaryHeadingLabel.trim()}
            </h2>
          )}
          {secondaryHeadingLabel && (
            <h3
              data-testid="sub-text"
              style={{
                color: isDarkMode && subTitleColorDark ? subTitleColorDark : subTitleColor,
                fontSize: isDesktopSize ? subTitleSizeDesktop : subTitleSizeMobile,
              }}
              className={subTextClassName}
              data-ffid="lbl-related-product-banner-sub-title"
            >
              {secondaryHeadingLabel.trim()}
            </h3>
          )}
        </div>
        <ProductGridWrapper ffid="related">
          {products.map((item, index) => (
            <PlpProductTileWrapper
              ffid="related"
              key={item.productId}
              product={item}
              index={index}
              categories={categories}
              tileCTA={resolved?.tileCTA}
              textConfig={resolved?.textConfiguration?.[0]}
              tieredPrice={resolved?.tieredPrice}
              calendarOptions={resolved?.calendarOptions}
              onClick={() => {
                trackSelectItem('Related Products', [
                  mapProductToGA4Item(enableGA4(), item, { index, itemListName: 'Related Products' }),
                ]);
              }}
            />
          ))}
        </ProductGridWrapper>
      </ProductBannerWrapper>
    </div>
  ) : null;
};

export default RelatedProductsBanner;
